import React, { useState, useEffect } from "react";
import { Table, Button, Input, Space, Tag, Modal, Form, Switch, notification, Popconfirm, InputNumber, Radio } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import PreferenceServ from "../../service/PreferenceServ";
import Breadcrumbs from "../../component/Breadcrumbs";
import { usePreference } from "../../context/PreferenceContext";
import EmptyState from "../../util/EmptyState";
import GlobalTooltip from "../../component/GlobalTooltip";
import { PrimaryButton } from "../../component/ui/ActionButtons";

const ManageCurrency = () => {
    const { t } = usePreference();
    const [currencies, setCurrencies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentCurrency, setCurrentCurrency] = useState(null);
    const [form] = Form.useForm();

    const fetchCurrencies = async () => {
        try {
            setLoading(true);
            const res = await PreferenceServ.getCurrencies({ all: 'true' });
            setCurrencies(res || []);
        } catch (error) {
            console.error("Failed to fetch currencies", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCurrencies();
    }, []);

    const handleToggleStatus = async (record) => {
        try {
            if (record.isDefault && record.isActive) {
                notification.warning({
                    message: t('cannot_deactivate_default_currency'),
                    description: t('set_another_default_first')
                });
                return;
            }

            const newStatus = !record.isActive;
            await PreferenceServ.updateCurrency(record._id, { isActive: newStatus });
            notification.success({ message: t('status_updated') });
            fetchCurrencies();
        } catch (error) {
            notification.error({ message: t('operation_failed') });
        }
    };

    const handleSetDefault = async (record) => {
        try {
            if (!record.isActive) {
                notification.warning({ message: t('activate_before_default') });
                return;
            }

            if (record.isDefault) return;

            await PreferenceServ.updateCurrency(record._id, { isDefault: true, isActive: true });
            notification.success({ message: t('default_currency_updated') });
            fetchCurrencies();
        } catch (error) {
            notification.error({ message: t('operation_failed') });
        }
    };

    const handleAdd = () => {
        setIsEditing(false);
        setCurrentCurrency(null);
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setIsEditing(true);
        setCurrentCurrency(record);
        setIsModalVisible(true);
    };

    // Use effect to sync form values when modal opens or currentCurrency changes
    useEffect(() => {
        if (isModalVisible) {
            if (isEditing && currentCurrency) {
                // Force boolean values for switches to ensure they display correctly
                const normalized = {
                    ...currentCurrency,
                    isActive: currentCurrency.isActive === true || currentCurrency.isActive === 1 || currentCurrency.isActive === 'true',
                    isDefault: currentCurrency.isDefault === true || currentCurrency.isDefault === 1 || currentCurrency.isDefault === 'true',
                };
                form.setFieldsValue(normalized);
            } else {
                form.resetFields();
                form.setFieldsValue({ isActive: true, isDefault: false, exchangeRate: 1 });
            }
        }
    }, [isModalVisible, isEditing, currentCurrency, form]);

    // Handle Default Switch change in Modal
    const onDefaultChange = (checked) => {
        if (checked) {
            form.setFieldsValue({ isActive: true });
        }
    };

    const handleDelete = async (id) => {
        try {
            await PreferenceServ.deleteCurrency(id);
            notification.success({ message: t('currency_deleted') });
            fetchCurrencies();
        } catch (error) {
            notification.error({ message: t('failed_delete_currency') });
        }
    };

    const handleSubmit = async (values) => {
        try {
            if (values.isDefault) {
                values.isActive = true;
            }

            if (isEditing && currentCurrency) {
                await PreferenceServ.updateCurrency(currentCurrency._id, values);
                notification.success({ message: t('currency_updated') });
            } else {
                await PreferenceServ.addCurrency(values);
                notification.success({ message: t('currency_added') });
            }
            setIsModalVisible(false);
            fetchCurrencies();
        } catch (error) {
            notification.error({ message: t('operation_failed'), description: error.message });
        }
    };

    const columns = [
        { title: t('currency_name'), dataIndex: "name", key: "name" },
        { title: t('code'), dataIndex: "code", key: "code" },
        { title: t('symbol'), dataIndex: "symbol", key: "symbol" },
        { title: t('exchange_rate'), dataIndex: "exchangeRate", key: "rate" },
        {
            title: (
                <GlobalTooltip title={t('system_default_tooltip')}>
                    {t('system_default')}
                </GlobalTooltip>
            ),
            key: "isDefault",
            align: 'center',
            render: (_, record) => (
                <Radio
                    checked={record.isDefault}
                    onChange={() => handleSetDefault(record)}
                />
            )
        },
        {
            title: t('active_status_label'),
            key: "isActive",
            align: 'center',
            render: (_, record) => (
                <Switch
                    checked={record.isActive}
                    onChange={() => handleToggleStatus(record)}
                    disabled={record.isDefault}
                />
            )
        },
        {
            title: t('actions'),
            key: "actions",
            render: (_, record) => (
                <Space>
                    <GlobalTooltip title={t('edit')}>
                        <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    </GlobalTooltip>
                    {!record.isDefault && (
                        <Popconfirm title={t('confirm_delete_currency')} onConfirm={() => handleDelete(record._id)}>
                            <Button danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                    )}
                </Space>
            )
        }
    ];

    return (
        <div className="pcu-padding-24">
            <div className="pcu-margin-bottom-16">
                <Breadcrumbs />
            </div>

            <div className="main-content-card pcu-bg-white pcu-radius-16 pcu-shadow-sm pcu-overflow-hidden">
                <div className="pcu-padding-x-24 pcu-padding-top-24 pcu-padding-bottom-16 pcu-border-bottom pcu-flex pcu-justify-between pcu-items-center">
                    <h1 className="pcu-font-22 pcu-font-700 pcu-color-title pcu-margin-0">
                        {t('manage_currency')}
                    </h1>
                    <PrimaryButton 
                        onClick={handleAdd} 
                        text={t('add_currency')} 
                        icon={<PlusOutlined />} 
                    />
                </div>

                <div className="pcu-padding-24">
                <Table
                    columns={columns}
                    dataSource={currencies}
                    rowKey="_id"
                    locale={{
                        emptyText: (
                            <EmptyState
                                title={t('no_currencies_found')}
                                description={t('no_currencies_description')}
                                gradient="blue"
                                onAction={handleAdd}
                                actionText={t('add_currency')}
                                icon="inbox"
                            />
                        )
                    }}
                    loading={loading}
                    bordered
                    pagination={{
                        position: ['bottomRight'],
                        showTotal: (total) => <span className="pagination-total">Total {total} items</span>
                    }}
                />

                <Modal
                    title={isEditing ? t('edit_currency') : t('add_currency')}
                    open={isModalVisible}
                    onCancel={() => setIsModalVisible(false)}
                    onOk={() => form.submit()}
                    okText={isEditing ? t('update') : t('save')}
                    cancelText={t('cancel')}
                    destroyOnClose
                >
                    <Form form={form} layout="vertical" onFinish={handleSubmit}>
                        <Form.Item name="name" label={t('currency_name')} rules={[{ required: true, message: t('please_enter_name') }]}>
                            <Input placeholder={t('currency_name_placeholder')} />
                        </Form.Item>
                        <Space className="pcu-flex pcu-margin-bottom-8" align="baseline">
                            <Form.Item name="code" label={t('code')} rules={[{ required: true, message: t('please_enter_code') }]}>
                                <Input placeholder={t('currency_code_placeholder')} />
                            </Form.Item>
                            <Form.Item name="symbol" label={t('symbol')} rules={[{ required: true, message: t('please_enter_symbol') }]}>
                                <Input placeholder={t('currency_symbol_placeholder')} />
                            </Form.Item>
                        </Space>
                        <Form.Item name="exchangeRate" label={t('exchange_rate')} rules={[{ required: true, message: t('please_enter_rate') }]}>
                            <InputNumber className="pcu-w-100" step={0.01} />
                        </Form.Item>

                        <div className="pcu-bg-main pcu-padding-15 pcu-radius-8 pcu-margin-top-10">
                            {/* Force active if default */}
                            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.isDefault !== currentValues.isDefault}>
                                {({ getFieldValue }) => {
                                    const isDefault = getFieldValue('isDefault');
                                    return (
                                        <Form.Item name="isActive" valuePropName="checked" className="pcu-margin-bottom-10">
                                            <Switch
                                                checkedChildren={t('active')}
                                                unCheckedChildren={t('active')}
                                                disabled={isDefault}
                                            />
                                            <span className="pcu-margin-left-8">{t('active_status_label')}</span>
                                        </Form.Item>
                                    );
                                }}
                            </Form.Item>

                            <Form.Item name="isDefault" valuePropName="checked" className="pcu-margin-0">
                                <Switch
                                    checkedChildren={t('default')}
                                    unCheckedChildren={t('standard')}
                                    onChange={onDefaultChange}
                                />
                                <span className="pcu-margin-left-8">{t('set_default_currency')}</span>
                            </Form.Item>
                        </div>
                    </Form>
                </Modal>
                </div>
            </div>
        </div>
    );
};

export default ManageCurrency;

